// ============================================================================
// GATE VIEW — GP New Referral Flow (Single-Scroll)
//
// Layout (one page, no step transitions):
//   [1] Patient Information  — PatientStepPHI (collapses to saved banner)
//   [2] Referral Routing     — searchable specialty + pathway picker (collapses to summary)
//   [3] Clinical Form        — appears after pack selected; submit at bottom
//   [submitted]              — receipt replaces the page after submit
//
// PHI Architecture (Solution A):
//   - Draft referral created on mount to get a referralId before PHI is written
//   - PHI saved via Cloud Functions (Admin SDK) in section 1 — never direct Firestore
//   - Submit passes the same draftReferralId to submitReferral() which overwrites
//     the draft doc with the full referral data atomically
//   - Submit is blocked unless phiSaved === true
//
// ZERO PHI in referral doc: No patient name, DOB, MRN, phone, email, address.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Shield, AlertTriangle, CheckCircle, Clock,
  Brain, Zap, Hand, Activity, Sparkles, Eye, Lock, Loader2,
  FileText, Clipboard, CheckSquare, Square,
  Copy, AlertCircle, RefreshCw, RotateCcw,
  ShieldOff, ChevronDown, ChevronUp, User, Paperclip, Download, Home, Upload, ClipboardList,
} from 'lucide-react';
import {
  getSpecialties, getPacks,
  isSpecialist,
  isIntakeCoordinator,
  isGpEmployee,
  computePathwayDecision,
  getActiveRedirectResult,
  PATHWAY_ENGINE_VERSION,
  normalizePackDoc,
  validateEnginePack,
  submitReferral,
  upsertPatientIdentity,
  upsertContact,
  upsertInsurance,
  getPatientIdentity,
  getContact,
  getInsurance,
  listAttachments,
  getWorkflowState,
  OVERRIDE_REASON_LABELS,
  OVERRIDE_NOTE_MAX_LENGTH,
  buildOverrideRecord,
} from '../core/index.js';
import { useAuth } from "../auth/AuthContext.jsx";
import PatientStepPHI from './Gate/PatientStepPHI.jsx';
import AttachmentsStepPHI from './Gate/AttachmentsStepPHI.jsx';
import { createAttachmentUploadUrl, finalizeAttachmentUpload, generateReferralPacketPdf } from '../core/phiAttachmentsApi.js';
import PacketContextPanel from './Gate/PacketContextPanel.jsx';
import {
  ClinicalEvidenceProvider,
  ClinicalEvidenceNote,
  FieldEvidenceChips,
  RedFlagEvidenceChips,
  AttachmentEvidenceChips,
} from './Gate/ClinicalEvidenceChips.jsx';
import {
  promotePacketDocuments,
  completePacket,
  releasePacketDraft,
  subscribePacket,
} from '../core/store.packets.js';
import { isSmartLaunchPacket } from '../core/smartLaunchGate.js';
import { buildEhrNoteNoPhi } from '../core/receipt/buildEhrNoteNoPhi.js';
import { buildPatientApprovedNoPhi } from '../core/messaging/buildPatientApprovedNoPhi.js';
import { buildPatientRequestNoPhi } from '../core/messaging/buildPatientRequestNoPhi.js';
import { buildFullReceiptModel } from '../core/receipt/buildFullReceiptModel.js';
import { renderReceiptPdf } from '../core/receipt/renderReceiptPdf.js';
import { writeExportAudit, EXPORT_AUDIT_EVENTS } from '../core/receipt/writeExportAudit.js';
import MessagingCard from './Gate/MessagingCard.jsx';
import RoutingSelector from './Gate/RoutingSelector.jsx';
import { saveRoutingDraft } from '../core/store.referralDrafts.js';
import { sanitizeErrorForOps } from '../core/sensitiveDataPolicy.js';

const ICONS = { Brain, Zap, Hand, Activity, Sparkles, Eye, Shield, AlertTriangle };

const VISIT_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'in-person', label: 'In-Person' },
  { value: 'video', label: 'Video (Telehealth)' },
  { value: 'phone', label: 'Phone (Telehealth)' },
  { value: 'async', label: 'Async Message' },
];

const VISIT_PURPOSE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'NEW_PATIENT_INITIAL_CONSULT', label: 'New Patient / Initial Consult' },
  { value: 'FOLLOW_UP',                  label: 'Follow-up' },
  { value: 'PROCEDURE_TESTING',          label: 'Procedure / Testing' },
  { value: 'SECOND_OPINION',             label: 'Second Opinion' },
  { value: 'URGENT_CONSULT',             label: 'Urgent Consult' },
];

// generateReceipt removed — replaced by buildEhrNoteNoPhi / buildFullReceiptModel

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Full-page error for load failures
const ErrorPanel = ({ title, message, onRetry }) => (
  <div className="p-8 bg-red-500/10 border border-red-500/30 rounded-2xl text-center">
    <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
      <AlertCircle className="w-8 h-8 text-red-400" />
    </div>
    <h3 className="text-xl font-semibold text-red-400 mb-2">{title}</h3>
    <p className="text-slate-400 mb-6">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 rounded-xl text-white font-medium mx-auto"
      >
        <RefreshCw className="w-5 h-5" /> Retry Connection
      </button>
    )}
  </div>
);

// Inline clinical error boundary for pack read failures
const PackErrorBoundary = ({ message, onRetry }) => (
  <div className="p-5 bg-red-500/10 border border-red-500/30 rounded-xl">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-medium text-red-400">
          Unable to reach Clinical Standards Database. Please contact IT.
        </p>
        {message && <p className="text-sm text-slate-400 mt-1">{message}</p>}
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex-shrink-0"
        >
          <RefreshCw className="w-4 h-4" /> Retry Connection
        </button>
      )}
    </div>
  </div>
);

// Live preview card — updates on every input change
const LivePreviewCard = ({ decision }) => {
  if (!decision) return null;

  const configs = {
    urgent: {
      color: 'red', label: 'URGENT', Icon: AlertTriangle,
      desc: 'Red flag detected — urgent pathway required',
    },
    routine: {
      color: 'emerald', label: 'ROUTINE', Icon: CheckCircle,
      desc: 'All requirements met — eligible for referral',
    },
    not_yet: {
      color: 'amber', label: 'NOT YET', Icon: Clock,
      desc: 'Missing required data — referral cannot proceed yet',
    },
  };
  const cfg = configs[decision.outcomeStatus] || configs.routine;
  const { color: c, Icon } = cfg;

  return (
    <div className={`p-5 rounded-2xl bg-${c}-500/10 border border-${c}-500/30`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-xl bg-${c}-500/20 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${c}-400`} />
        </div>
        <div className="flex items-baseline gap-2">
          <span className={`text-xl font-bold text-${c}-400`}>{cfg.label}</span>
          <span className="text-xs text-slate-500 font-normal">Live Preview</span>
        </div>
      </div>
      <p className={`text-sm text-${c}-400/80 ml-1`}>{cfg.desc}</p>

      {decision.missingRequiredFields?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-700/30">
          <p className="text-xs font-medium text-slate-400 mb-2">Missing required items:</p>
          <div className="space-y-1">
            {decision.missingRequiredFields.map(f => (
              <div key={f.id} className="flex items-center gap-2 text-xs text-amber-400">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                {f.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {decision.triggeredRedFlags?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-700/30">
          <p className="text-xs font-medium text-slate-400 mb-2">Red flags triggered:</p>
          <div className="space-y-1">
            {decision.triggeredRedFlags.map(rf => (
              <div key={rf.id} className="flex items-center gap-2 text-xs text-red-400">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                {rf.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Severity slider for numeric/severity fields
const SeveritySlider = ({ value, onChange, min = 0, max = 10 }) => {
  const current = value ?? 0;
  const pct = ((current - min) / (max - min)) * 100;
  const color = current <= 3 ? 'emerald' : current <= 6 ? 'amber' : 'red';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">Mild</span>
        <span className={`text-lg font-bold text-${color}-400`}>{current}/{max}</span>
        <span className="text-sm text-slate-400">Severe</span>
      </div>
      <div className="relative">
        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${
              color === 'emerald' ? 'from-emerald-600 to-emerald-400' :
              color === 'amber' ? 'from-amber-600 to-amber-400' : 'from-red-600 to-red-400'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <input
          type="range" min={min} max={max} step={1} value={current}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>
    </div>
  );
};

const SelectChevron = () => (
  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
    <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  </div>
);

// ============================================================================
// MAIN GATE VIEW COMPONENT
// ============================================================================

const GateView = ({ onNavigate, activeFocusTarget, navState, onLearningContext }) => {
  const { user } = useAuth();

  // Data loading
  const [specialties, setSpecialties] = useState({});
  const [rawPacks, setRawPacks] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Patient form data — set locally when PatientStepPHI continues; written via CFs on submit
  const [patientFormData, setPatientFormData] = useState(null);
  // phiSaved is a separate flag so "Edit" can re-open the form without losing the stored data
  const [phiSaved, setPhiSaved] = useState(false);
  // Build 2J P2 — never-PHI EHR candidate usage (refs/counts) captured from the PHI
  // step; threaded into submitReferral to record audit provenance. Not PHI.
  const [ehrCandidateUsage, setEhrCandidateUsage] = useState(null);
  // phiLoading: true while fetching PHI during resume (shows spinner instead of empty form)
  const [phiLoading, setPhiLoading] = useState(false);
  // isResuming: true when opening from Not-Yet Inbox — shows full-page loader until PHI + pack are ready
  const [isResuming, setIsResuming] = useState(() => Boolean(sessionStorage.getItem('resumeReferral')));
  // resumeReferralId: the original referral being completed (set during resume).
  // Kept in state so handleDownloadPhiPdf can merge attachments from BOTH referral IDs —
  // the original (where files were uploaded) and the new submission (created on re-submit).
  const [resumeReferralId, setResumeReferralId] = useState(null);

  // Phase 3 — PHI retry protection. Once the atomic core referral batch commits,
  // we record its referralId so a failure in the PHI/attachment/packet steps can
  // RETRY only those steps against the SAME referral, instead of re-running
  // submitReferral and creating a duplicate referral/metric/audit/workflow/lock.
  // Refs mirror the state for immediate SYNCHRONOUS access inside the same
  // handleSubmit try/catch (React state updates are async). Distinct from
  // resumeReferralId, which is the genuine NOT_YET inbox-resume id.
  const [committedReferralId, setCommittedReferralId] = useState(null);
  const committedReferralIdRef = useRef(null);
  const committedSubmitResultRef = useRef(null);

  // Phase: 'form' (single-scroll main flow) | 'submitted'
  const [phase, setPhase] = useState('form');

  // ── Packet-assisted Gate context (Phase 1) ───────────────────────────────
  // packetId arrives via navState (the handleNavigate(view, state) payload set
  // when the GP clicked "Start New Referral" from a packet in the Inbox).
  // NOTE: activeFocusTarget is a guided-learning UI highlight target (string|null),
  // NOT navigation state — reading packetId from it always yielded null, which
  // silently skipped packet promotion. Manual Gate (no packetId) is the default
  // and must continue to work byte-for-byte unchanged.
  const packetId = navState && typeof navState === 'object'
    ? navState.packetId || null
    : null;
  const [selectedPacketDocumentIds, setSelectedPacketDocumentIds] = useState(null);
  // Recovery-state markers when post-submit promotion partially fails. We
  // deliberately DO NOT advance the UI to 'submitted' if any promotion
  // fails — the user sees a recovery card with a Retry button.
  const [packetRecovery, setPacketRecovery] = useState(null); // { referralId, failedIds, message }
  const [packetRetrying, setPacketRetrying] = useState(false);
  // Never-PHI packet root for the started packet — the sole source of EHR-origin
  // truth. EHR-launch packets (sourceType 'ehr_launch') are candidate-driven and
  // may submit with zero selected documents; null until loaded → treated as a
  // document packet (empty selection keeps blocking — safe default).
  const [packetRoot, setPacketRoot] = useState(null);
  // Best-effort release of DRAFTING_REFERRAL lock if the user navigates away
  // before submitting. Janitor for stuck packets is out of scope (see follow-up).
  useEffect(() => {
    if (!packetId) return undefined;
    return () => {
      // Only release if the user abandoned without submitting (phase !== 'submitted').
      // We read phase via a closure-safe ref-equivalent by checking submitResult.
      if (!submitResult) {
        releasePacketDraft(packetId).catch((err) => {
          console.warn('[GateView] releasePacketDraft on unmount failed (non-blocking):', err?.message);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId]);

  // Load the never-PHI packet root so the submit guard can distinguish an EHR-launch
  // packet (zero documents by design) from a document-upload packet. Live subscription
  // so the panel/guard react if the EHR import rollup lands after the GP navigated in.
  useEffect(() => {
    if (!packetId || !user?.tenantId) { setPacketRoot(null); return undefined; }
    const unsub = subscribePacket(user.tenantId, packetId, (row) => setPacketRoot(row || null));
    return () => { try { if (unsub) unsub(); } catch (_) { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId, user?.tenantId]);
  // EHR-origin is derived ONLY from the never-PHI packet root sourceType (via the
  // shared pure helper). Null/not-yet-loaded → false → behaves as a document packet.
  const isEhrLaunchPacket = isSmartLaunchPacket(packetRoot);

  useEffect(() => {
    if (onLearningContext) {
      onLearningContext({ isResuming: isResuming || !!resumeReferralId, isSubmitted: phase === 'submitted' });
    }
    return () => { if (onLearningContext) onLearningContext({}); };
  }, [isResuming, resumeReferralId, phase, onLearningContext]);

  // Specialty + Pack selection
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(null);
  const [normalizedPack, setNormalizedPack] = useState(null);
  const [packLoadError, setPackLoadError] = useState(null);

  // Header fields (non-PHI required fields, enum only)
  const [visitType, setVisitType] = useState('');
  const [reasonForEscalation, setReasonForEscalation] = useState('');
  const [visitPurpose, setVisitPurpose] = useState('');

  // Redirect flags V2 — evaluated BEFORE clinical form; never persisted as outcome or workflow state.
  // Unified single-selection: at most one routing option across BOTH routing blocks may be active.
  // Shape: { block: 'intra' | 'cross', flagId: string } | null
  const [selectedRoutingFlag, setSelectedRoutingFlag] = useState(null);
  // Inline error for intra-specialty switch (target pack not found)
  const [intraPackSwitchError, setIntraPackSwitchError] = useState(null);
  // Inline error for cross-specialty switch (missing targetSpecialtyId or ambiguous target pack)
  const [crossPackSwitchError, setCrossPackSwitchError] = useState(null);
  // Pre-seeds RoutingSelector with a target specialty when cross-specialty fallback triggers
  const [redirectFallbackSpecialtyId, setRedirectFallbackSpecialtyId] = useState(null);
  // Pack selected by user when redirect target specialty has multiple packs (ambiguous state)

  // Clinical inputs rendered from pack schema
  const [selectedRedFlagIds, setSelectedRedFlagIds] = useState([]);
  const [clinicalAnswers, setClinicalAnswers] = useState({});

  // Attachments — staged locally; uploaded after submitReferral() provides a referralId
  // Each entry: { id, file, type, originalName, status: 'staged'|'uploading'|'ready'|'failed', sizeBytes? }
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const packAttachInputRef = useRef(null);
  const pendingAttachTypeRef = useRef(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);

  // Receipt export state (shown after submit)
  const [ehrCopied, setEhrCopied] = useState(false);
  const [ehrCopyLoading, setEhrCopyLoading] = useState(false);
  const [ehrCopyError, setEhrCopyError] = useState(null);
  const [ehrFallbackNote, setEhrFallbackNote] = useState(null); // non-null = show clipboard-fail modal
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  // Patient note copy card (receipt page)
  const [patientNoteCopied, setPatientNoteCopied] = useState(false);
  const [patientNoteCopyError, setPatientNoteCopyError] = useState(null);


  // Override state — only active when liveDecision is not_yet
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  // Snapshot of override info captured at submit time (for receipt display)
  const [submittedOverride, setSubmittedOverride] = useState(null);

  // Routing draft (Routing Selector)
  const [draftSaving,    setDraftSaving]    = useState(false);
  const [draftSaveError, setDraftSaveError] = useState(null);
  const [draftInvalid,   setDraftInvalid]   = useState(false);

  // Deterministic draft ID — one active routing draft per user
  const routingDraftId = useMemo(
    () => (user?.uid || user?.id) ? `routing_${user.uid || user.id}` : null,
    [user?.uid, user?.id],
  );

  // Role checks
  const userIsSpecialist = isSpecialist(user?.role);
  const allowedSpecialtyIds = userIsSpecialist ? (user?.specialtyIds || []) : null;

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadData = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [specialtiesData, packsData] = await Promise.all([
        getSpecialties(user.tenantId),
        getPacks(user.tenantId),
      ]);
      setSpecialties(specialtiesData || {});
      setRawPacks(packsData || {});
    } catch (err) {
      console.error('[GateView Error] Loading pathway data:', err);
      setLoadError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Resume from Not-Yet Inbox ────────────────────────────────────────────────
  // When the GP clicks "Complete This Referral" in NotYetInboxView, that view writes
  // the original referral data to sessionStorage under 'resumeReferral'.
  // This effect runs once rawPacks is populated and restores the full gate state:
  //   · specialty + pack selection (normalizedPack)
  //   · clinical answers, red-flag selections
  //   · visit type, reason for escalation
  //   · PHI — fetched via Cloud Functions using resume.referralId, then injected into
  //     patientFormData and phiSaved=true so the gate is immediately submittable
  // sessionStorage entry is consumed immediately to prevent stale restores on subsequent visits.
  useEffect(() => {
    if (!rawPacks || Object.keys(rawPacks).length === 0) return;

    const stored = sessionStorage.getItem('resumeReferral');
    if (!stored) { setIsResuming(false); return; }

    let resume;
    try { resume = JSON.parse(stored); } catch { sessionStorage.removeItem('resumeReferral'); setIsResuming(false); return; }
    sessionStorage.removeItem('resumeReferral'); // consume immediately

    const {
      specialtyId, packId, versionId, answers, triggeredRedFlagIds,
      telehealthContext,
      visitType:            resumeVisitType,
      reasonForEscalation:  resumeEscalation,
      visitPurpose:         resumeVisitPurpose,
    } = resume;
    if (!specialtyId || !packId) { setIsResuming(false); return; }

    console.log('[GateView resume] payload:', {
      referralId: resume.referralId, specialtyId, packId, versionId,
      visitType: resumeVisitType, reasonForEscalation: resumeEscalation,
      hasTelehealthContext: Boolean(telehealthContext),
    });

    // Resolve the target pack — prefer exact packId + versionId + specialtyId match,
    // fall back to packId + specialtyId (latest available version).
    const allPacks = Object.values(rawPacks);
    const targetEntry =
      allPacks.find(p => p.packId === packId && p.versionId === versionId && p.specialtyId === specialtyId) ||
      allPacks.find(p => p.packId === packId && p.specialtyId === specialtyId);

    if (!targetEntry) {
      console.warn('[GateView resume] Pack not found for resume — specialtyId:', specialtyId, 'packId:', packId);
      setIsResuming(false); return;
    }

    const normalized = normalizePackDoc(targetEntry, targetEntry.packDocId, specialtyId);
    if (!normalized) { console.warn('[GateView resume] Pack normalization failed'); setIsResuming(false); return; }

    const validation = validateEnginePack(normalized);
    if (!validation.valid) { console.warn('[GateView resume] Pack validation failed', validation.errors); setIsResuming(false); return; }

    // Seed severity fields at 0 then overlay saved answers so resumed values win.
    const initialAnswers = {};
    normalized.fields?.forEach(f => { if (f.type === 'severity') initialAnswers[f.id] = 0; });
    const mergedAnswers = { ...initialAnswers, ...(answers || {}) };

    // Resolve visitType.
    // Priority 1: top-level resumeVisitType (schema v2+, lowercase: 'video'/'phone'/'async'/'in-person')
    // Priority 2: reverse-map telehealthContext.visitType for pre-schema-fix referrals ('Video'→'video')
    // Priority 3: no telehealthContext on old doc → must have been 'in-person'
    const resolvedVisitType =
      resumeVisitType ||
      (telehealthContext?.visitType ? telehealthContext.visitType.toLowerCase() : 'in-person');

    // Resolve reasonForEscalation.
    // Priority 1: top-level resumeEscalation (schema v2+)
    // Priority 2: inside telehealthContext (legacy storage)
    const resolvedEscalation =
      resumeEscalation ||
      telehealthContext?.reasonForEscalation ||
      '';

    // Set all state in one synchronous batch.
    setSelectedSpecialtyId(specialtyId);
    setNormalizedPack(normalized);
    setPackLoadError(null);
    setSelectedRoutingFlag(null);
    setIntraPackSwitchError(null);
    setCrossPackSwitchError(null);
    setSelectedRedFlagIds(triggeredRedFlagIds || []);
    setClinicalAnswers(mergedAnswers);
    setVisitType(resolvedVisitType);
    setReasonForEscalation(resolvedEscalation);
    setVisitPurpose(resumeVisitPurpose || '');

    // PHI + attachments load — fetch all PHI and existing attachment manifests
    // via Cloud Functions. Sets patientFormData + phiSaved=true + pre-populates
    // attachmentFiles (status:'ready') so the gate is immediately submittable and
    // the attachments section shows what was already uploaded in the first run.
    // resume.referralId is the canonical field; resume.id is the Firestore doc ID fallback
    // (identical values — guard against docs where the field was absent).
    const storedReferralId = resume.referralId || resume.id || null;
    if (!storedReferralId) {
      console.error('[GateView resume] referralId missing from resume payload — cannot resume in update mode.', resume);
    }
    if (storedReferralId) {
      console.log('[GateView resume] Fetching PHI + attachments for referralId:', storedReferralId);
      setResumeReferralId(storedReferralId);
      setPhiLoading(true);
      const loadPhiForResume = async () => {
        try {
          const [idRes, ctRes, insRes, attRes] = await Promise.all([
            getPatientIdentity(storedReferralId),
            getContact(storedReferralId),
            getInsurance(storedReferralId).catch(() => ({ exists: false, data: null })),
            listAttachments(storedReferralId).catch(() => ({ attachments: [] })),
          ]);
          console.log('[GateView resume] PHI + attachments loaded:', {
            hasIdentity:  Boolean(idRes?.data),
            hasContact:   Boolean(ctRes?.data),
            hasInsurance: Boolean(insRes?.data),
            attachmentCount: (attRes?.attachments || []).length,
          });
          setPatientFormData({
            identity: idRes?.data  || null,
            contact:  ctRes?.data  || null,
            insurance: insRes?.data || null,
          });
          setPhiSaved(true);

          // Pre-populate attachmentFiles with already-uploaded manifests (status:'ready').
          // These entries are display-only — the upload loop skips non-staged entries,
          // and the remove button is hidden for ready entries in AttachmentsStepPHI.
          const existingAttachments = (attRes?.attachments || []).map(att => ({
            id:           att.attachmentId || att.id || `resume_${Math.random().toString(36).slice(2)}`,
            file:         null,  // already in Storage — no local File object needed
            type:         att.type || 'OTHER',
            originalName: att.originalName || att.attachmentId || 'Attachment',
            status:       'ready',
            sizeBytes:    att.sizeBytes ?? att.uploadedSizeBytes ?? null,
          }));
          if (existingAttachments.length > 0) {
            setAttachmentFiles(existingAttachments);
          }
        } catch (err) {
          // Redact before printing — a raw PHI-load error object could carry
          // PHI-adjacent context; log only a sanitized code/message.
          console.warn('[GateView resume] PHI load failed — patient info section will be empty', sanitizeErrorForOps(err));
          // phiSaved stays false; PatientStepPHI shows empty form so user can re-enter.
        } finally {
          setPhiLoading(false);
          setIsResuming(false);
        }
      };
      loadPhiForResume();
    } else {
      console.warn('[GateView resume] No referralId in resume payload — PHI cannot be loaded');
      setIsResuming(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawPacks]); // intentionally only rawPacks — runs once after first load completes

  // ============================================================================
  // DERIVED STATE
  // ============================================================================

  const filteredSpecialties = useMemo(() => {
    const entries = Object.entries(specialties);
    if (!allowedSpecialtyIds) return entries;
    return entries.filter(([id]) => allowedSpecialtyIds.includes(id));
  }, [specialties, allowedSpecialtyIds]);

  // Path 1 engine pack: strip field types that Path 1 cannot collect (text/textarea).
  // These are free-text fields blocked by the zero-PHI invariant. Removing them
  // from the pack means the engine won't count them as "missing required fields",
  // preventing perpetual Not-Yet on packs that define any required text fields.
  const path1Pack = useMemo(() => {
    if (!normalizedPack) return null;
    const BLOCKED = new Set(['text', 'textarea']);
    const filteredFields = (normalizedPack.fields || []).filter(
      f => !BLOCKED.has(f.type)
    );
    return { ...normalizedPack, fields: filteredFields };
  }, [normalizedPack]);

  // Live decision preview — pure function, recomputed on every input change, NO Firestore access
  const liveDecision = useMemo(() => {
    if (!path1Pack) return null;
    return computePathwayDecision({
      pack: path1Pack,
      answers: clinicalAnswers,
      selectedRedFlagIds,
      patientContext: {},
    });
  }, [path1Pack, clinicalAnswers, selectedRedFlagIds]);

  // Redirect check V2 — intra evaluated first, cross second; pure, never written to Firestore.
  // Derives intra/cross ID arrays from the unified selectedRoutingFlag (at most one selection total).
  const activeRedirectResult = useMemo(() => {
    if (!normalizedPack) return { matched: false, level: null, flag: null };
    const intraIds = selectedRoutingFlag?.block === 'intra' ? [selectedRoutingFlag.flagId] : [];
    const crossIds = selectedRoutingFlag?.block === 'cross' ? [selectedRoutingFlag.flagId] : [];
    return getActiveRedirectResult(normalizedPack, intraIds, crossIds);
  }, [normalizedPack, selectedRoutingFlag]);
  const hasRedirect = activeRedirectResult.matched;

  // ── Cross-specialty pack resolution ──────────────────────────────────────────────────────
  // Deterministic resolution order (no fuzzy matching):
  //   1. flag.target.targetPackId  — exact pack id specified in the redirect flag
  //   2. flag.target.packId        — legacy alias
  //   3. fallback: inspect active packs under the target specialty
  //      - exactly one  → use it
  //      - multiple     → ambiguous; block CTA and show error
  //      - zero         → no_packs; fall back to RoutingSelector
  //
  // Returns: { state: 'resolved', entry, specialtyId }
  //        | { state: 'ambiguous', message }
  //        | { state: 'no_packs', specialtyId }
  //        | { state: 'error', message }
  //        | null  (no cross redirect is active)
  const crossResolution = useMemo(() => {
    if (activeRedirectResult.level !== 'cross_specialty' || !activeRedirectResult.flag) return null;
    const flag = activeRedirectResult.flag;
    const targetSpecialtyId = flag.target?.targetId;
    if (!targetSpecialtyId) {
      return { state: 'fallback', specialtyId: null };
    }
    // 1 + 2: exact pack id on the flag
    const exactPackId = flag.target?.targetPackId || flag.target?.packId;
    if (exactPackId) {
      const entry = Object.values(rawPacks).find(
        p => p.packId === exactPackId && p.specialtyId === targetSpecialtyId
      );
      if (entry) return { state: 'resolved', entry, specialtyId: targetSpecialtyId };
      // Exact pack not found — fall back to routing selector instead of blocking
      return { state: 'fallback', specialtyId: targetSpecialtyId };
    }
    // 3: fallback — inspect active packs under target specialty
    const candidates = Object.values(rawPacks).filter(
      p => p.specialtyId === targetSpecialtyId && p.status === 'active'
    );
    if (candidates.length === 1) return { state: 'resolved', entry: candidates[0], specialtyId: targetSpecialtyId };
    // Multiple or zero packs — fall back to routing selector
    return { state: 'fallback', specialtyId: targetSpecialtyId };
  }, [activeRedirectResult, rawPacks]);

  // Submit is enabled only when patient PHI saved, header fields filled, no pack error, and no active redirect
  const escalationRequired = visitPurpose === 'URGENT_CONSULT';
  const canSubmit = Boolean(phiSaved && visitType && visitPurpose && (!escalationRequired || reasonForEscalation) && !isSubmitting && !packLoadError && path1Pack && liveDecision && !hasRedirect);

  // Is the override active and fully confirmed?
  const overrideActive = overrideConfirmed && Boolean(overrideReason);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Clear override state whenever the live outcome moves away from not_yet.
  // This prevents stale overrides from carrying over if the GP fixes the missing data.
  useEffect(() => {
    if (liveDecision?.outcomeStatus !== 'not_yet') {
      setOverrideOpen(false);
      setOverrideReason('');
      setOverrideNote('');
      setOverrideConfirmed(false);
    }
  }, [liveDecision?.outcomeStatus]);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, [activeFocusTarget]);


  // ============================================================================
  // HANDLERS
  // ============================================================================

  function resetFlow() {
    setPatientFormData(null);
    setPhiSaved(false);
    setPhase('form');
    setDraftSaving(false);
    setDraftSaveError(null);
    setDraftInvalid(false);
    setAttachmentFiles([]);
    setSelectedSpecialtyId(null);
    setNormalizedPack(null);
    setPackLoadError(null);
    setVisitType('');
    setReasonForEscalation('');
    setSelectedRoutingFlag(null);
    setIntraPackSwitchError(null);
    setCrossPackSwitchError(null);
    setSelectedRedFlagIds([]);
    setClinicalAnswers({});
    setIsSubmitting(false);
    setSubmitResult(null);
    setSubmitError(null);
    setSubmittedAt(null);
    setEhrCopied(false);
    setEhrCopyError(null);
    setPdfLoading(false);
    setPdfError(null);
    setOverrideOpen(false);
    setOverrideReason('');
    setOverrideNote('');
    setOverrideConfirmed(false);
    setSubmittedOverride(null);
    setRedirectFallbackSpecialtyId(null);
    setResumeReferralId(null);
    // Phase 3 — clear PHI-retry tracking so a new referral starts fresh.
    setCommittedReferralId(null);
    committedReferralIdRef.current = null;
    committedSubmitResultRef.current = null;
  }

  const handleSelectPack = useCallback((packDocId, rawPack, specialtyOverride = null) => {
    setPackLoadError(null);
    setSubmitError(null);
    setSubmitResult(null);

    // specialtyOverride lets callers bypass the stale-closure value when they
    // are simultaneously updating selectedSpecialtyId in the same event cycle.
    const effectiveSpecialtyId = specialtyOverride || selectedSpecialtyId;
    const normalized = normalizePackDoc(rawPack, packDocId, effectiveSpecialtyId);
    if (!normalized) {
      setPackLoadError('Pack data is invalid — cannot load clinical standards.');
      setNormalizedPack(null);
      return;
    }

    const validation = validateEnginePack(normalized);
    if (!validation.valid) {
      setPackLoadError(`Pack validation failed: ${validation.errors.join(', ')}`);
      setNormalizedPack(null);
      return;
    }

    // Initialize answers for severity fields
    const initialAnswers = {};
    normalized.fields?.forEach(field => {
      if (field.type === 'severity') initialAnswers[field.id] = 0;
    });

    setNormalizedPack(normalized);
    setSelectedRoutingFlag(null);   // reset unified routing selection on every pack switch
    setIntraPackSwitchError(null);
    setCrossPackSwitchError(null);
    setSelectedRedFlagIds([]);
    setClinicalAnswers(initialAnswers);
    setVisitType('');
    setReasonForEscalation('');
  }, [selectedSpecialtyId]);

  // ── Routing Selector: commit selection → write draft → proceed to clinical form ──

  const handleRoutingCommit = useCallback(async (specialtyId, packDocId, rawPack) => {
    if (!routingDraftId || !user?.tenantId) return;

    setDraftSaving(true);
    setDraftSaveError(null);

    const spec     = specialties[specialtyId];
    const packName = rawPack.name || rawPack.metadata?.name || packDocId;

    try {
      await saveRoutingDraft({
        tenantId:              user.tenantId,
        draftId:               routingDraftId,
        createdByUid:          user.uid || user.id,
        selectedSpecialtyId:   specialtyId,
        selectedSpecialtyName: spec?.name || specialtyId,
        selectedPackId:        packDocId,
        selectedPackName:      packName,
        selectedPackRef:       `tenants/${user.tenantId}/specialties/${specialtyId}/packs/${packDocId}`,
      });

      setDraftSaving(false);
      setDraftInvalid(false);
      setRedirectFallbackSpecialtyId(null); // consume the fallback seed once user commits

      // Proceed to clinical form — set specialty then select pack in one shot.
      // Pass specialtyId directly to avoid stale-closure issue in handleSelectPack.
      setSelectedSpecialtyId(specialtyId);
      handleSelectPack(packDocId, rawPack, specialtyId);
    } catch (err) {
      console.error('[GateView] Routing draft save failed:', err);
      setDraftSaving(false);
      setDraftSaveError(safeUserError(err));
    }
  }, [routingDraftId, user, specialties, handleSelectPack]);

  // Clear only pack selection (keeps patient info + specialty choice intact)
  const handleChangePack = useCallback(() => {
    setNormalizedPack(null);
    setPackLoadError(null);
    setSelectedSpecialtyId(null);
    setSelectedRoutingFlag(null);
    setIntraPackSwitchError(null);
    setCrossPackSwitchError(null);
    setSelectedRedFlagIds([]);
    setClinicalAnswers({});
    setVisitType('');
    setReasonForEscalation('');
    setSubmitError(null);
    setOverrideOpen(false);
    setOverrideReason('');
    setOverrideNote('');
    setOverrideConfirmed(false);
    setRedirectFallbackSpecialtyId(null);
  }, []);

  // In-place pack switch for same-specialty (intra) redirect.
  // Finds the target pack in rawPacks by packId and replaces normalizedPack in-place.
  // No navigation, no Firestore write, no referral doc created.
  const handleIntraPackSwitch = useCallback((flag) => {
    setIntraPackSwitchError(null);
    const targetPackId = flag?.target?.targetId;
    if (!targetPackId) {
      setIntraPackSwitchError('Target pack ID is missing from this redirect flag. Contact your administrator.');
      return;
    }
    const targetEntry = Object.values(rawPacks).find(p => p.packId === targetPackId);
    if (!targetEntry) {
      setIntraPackSwitchError(
        `Pack "${flag.target?.label || targetPackId}" is not available for this tenant. Contact your administrator.`
      );
      return;
    }
    // handleSelectPack resets all pack-specific state (routing flags, clinical answers, red flags).
    // Patient info and visit context are preserved.
    handleSelectPack(targetEntry.packDocId, targetEntry, selectedSpecialtyId);
  }, [rawPacks, selectedSpecialtyId, handleSelectPack]);

  // Cross-specialty switch — uses pre-computed crossResolution (no Firestore write).
  //
  // Outcome A — resolved: switch specialty + pack in-place.
  // Outcome B — no_packs: fall back to RoutingSelector pre-seeded to the target specialty.
  // Outcome ambiguous/error: show crossPackSwitchError, block switch.
  const handleCrossSpecialtySwitch = useCallback(() => {
    setCrossPackSwitchError(null);
    if (!crossResolution) return;
    if (crossResolution.state === 'error' && !crossResolution.specialtyId) {
      setCrossPackSwitchError(crossResolution.message);
      return;
    }
    if (crossResolution.state === 'resolved') {
      setSelectedSpecialtyId(crossResolution.specialtyId);
      handleSelectPack(crossResolution.entry.packDocId, crossResolution.entry, crossResolution.specialtyId);
      return;
    }
    if (crossResolution.specialtyId) {
      setRedirectFallbackSpecialtyId(crossResolution.specialtyId);
    }
    handleChangePack();
  }, [crossResolution, handleSelectPack, handleChangePack]);

  const handleRedFlagToggle = (flagId) => {
    setSelectedRedFlagIds(prev =>
      prev.includes(flagId) ? prev.filter(id => id !== flagId) : [...prev, flagId]
    );
  };

  const handleClinicalChange = (fieldId, value) => {
    setClinicalAnswers(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleAddAttachments = useCallback((fileList, type) => {
    const newEntries = fileList.map(file => {
      // Auto-correct type when file MIME doesn't match the server's PHOTO restriction.
      // PHOTO only allows image/* — if the user picked a PDF while PHOTO is selected,
      // switch it to PDF so the server doesn't reject the upload with a 400.
      const mime = file.type || '';
      const isPdf   = mime === 'application/pdf';
      const isImage = mime.startsWith('image/');
      let resolvedType = type;
      if (type === 'PHOTO' && !isImage) {
        // Non-image file staged as PHOTO → promote to PDF (or OTHER as fallback)
        resolvedType = isPdf ? 'PDF' : 'OTHER';
      }
      return {
        id: `att_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        file,
        type: resolvedType,
        originalName: file.name,
        status: 'staged',
      };
    });
    setAttachmentFiles(prev => [...prev, ...newEntries]);
  }, []);

  const handleRemoveAttachment = useCallback((id) => {
    setAttachmentFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit || !liveDecision) return;

    // Fail-fast: redirect is active — Gate contract forbids any Firestore write.
    // canSubmit already blocks this via !hasRedirect, but guard here too in case
    // of a race or stale closure.
    if (hasRedirect) {
      setSubmitError('This referral must be redirected before submission. No referral has been created.');
      return;
    }

    // Packet-assisted submit: document-upload packets always have ≥1 READY document
    // (markPacketDrafting enforces it) and PacketContextPanel auto-selects them, so an
    // empty selection here means the panel is still loading or the user deselected
    // everything — block rather than silently create a referral with no attachments.
    //
    // EHR-launch packets (sourceType 'ehr_launch') are candidate-driven and carry ZERO
    // packet documents by design, so an empty selection is valid for them. isEhrLaunchPacket
    // is derived from the never-PHI packet root; until that root loads it is false, so we
    // keep blocking (safe default). Pack-required attachments / Not-Yet logic are unaffected.
    const packetSelectionEmpty = !Array.isArray(selectedPacketDocumentIds) || selectedPacketDocumentIds.length === 0;
    if (packetId && packetSelectionEmpty && !isEhrLaunchPacket) {
      setSubmitError('Select at least one packet document to attach (or wait for the packet documents to finish loading) before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build override record if GP confirmed an override on a NOT_YET decision.
      // Computed once here so it is in scope for both the first-commit path and the
      // receipt; only written to Firestore on the first core commit.
      let override = null;
      if (overrideActive && liveDecision.outcomeStatus === 'not_yet') {
        override = buildOverrideRecord({
          reason: overrideReason,
          note: overrideNote.trim() || null,
          confirmedByUid: user.uid || user.id,
          confirmedByRole: user.role || 'gp_employee',
        });
      }

      // PHI RETRY GUARD (Phase 3): if a prior attempt already committed the core
      // referral batch (committedReferralIdRef set), SKIP submitReferral entirely
      // and reuse the same referral + result. This prevents a duplicate referral /
      // metric / auditLog / workflow / submission lock when only the PHI step (or
      // attachments / PDF / packet) had failed. Does NOT use submitReferral rerun
      // mode (no extra audit/metric/workflow events).
      let result;
      let referralId;
      if (committedReferralIdRef.current) {
        referralId = committedReferralIdRef.current;
        result = committedSubmitResultRef.current;
      } else {
        // Step 1: Atomic write — referral + metric + auditLog (all or nothing).
        // When resumeReferralId is set (NOT_YET rerun), update the existing doc in place
        // instead of creating a new referral. The original referralId is preserved.
        result = await submitReferral({
          tenantId: user.tenantId,
          specialtyId: normalizedPack.specialtyId,
          packId: normalizedPack.packId,
          versionId: normalizedPack.versionId,
          createdByUid: user.uid || user.id,
          createdByName: user.displayName || user.name || null,
          createdByEmail: user.email || null,
          createdByRole: user.role || null,
          patient: { visitType, reasonForEscalation, visitPurpose },
          answers: clinicalAnswers,
          outcome: {
            status: liveDecision.outcomeStatus,
            reason: liveDecision.rationale,
            completenessScore: liveDecision.completenessScore,
          },
          missingFieldIds: liveDecision.missingRequiredFieldIds || [],
          triggeredRedFlagIds: liveDecision.triggeredRedFlagIds || [],
          trace: liveDecision.trace || [],
          telehealthType: (visitType === 'video' || visitType === 'phone') ? visitType : null,
          engineVersion: PATHWAY_ENGINE_VERSION,
          durationMs: liveDecision.durationMs || 0,
          override,
          // RERUN: preserve the original referralId — update existing doc, do not create new.
          existingReferralId: resumeReferralId || null,
          // Build 2J P2 — never-PHI EHR candidate provenance (refs/counts only).
          ehrCandidateUsage,
        });
        referralId = result.referralId;
        // Record the committed referral IMMEDIATELY (ref = synchronous) so a failure
        // in the PHI/attachment/packet steps below retries the SAME referral rather
        // than creating a new one. State drives the retry UI on re-render.
        committedReferralIdRef.current = referralId;
        committedSubmitResultRef.current = result;
        setCommittedReferralId(referralId);
      }

      // Step 2: Write PHI via Cloud Functions now that the referral doc exists.
      // The referral doc (created above) satisfies the CF ownership check.
      // PHI is never written before the referral is committed. PHI upserts are
      // idempotent for a known referralId, so this is safe to re-run on retry.
      await upsertPatientIdentity(referralId, patientFormData.identity);
      await upsertContact(referralId, patientFormData.contact);
      if (patientFormData.insurance) {
        await upsertInsurance(referralId, patientFormData.insurance);
      }

      // Step 3: Upload staged attachments (best-effort — failures do not block receipt)
      // Uploads run sequentially so status chips update one-by-one before the receipt shows.
      const staged = attachmentFiles.filter(f => f.status === 'staged');
      for (const entry of staged) {
        setAttachmentFiles(prev =>
          prev.map(f => f.id === entry.id ? { ...f, status: 'uploading' } : f)
        );
        try {
          const { attachmentId, uploadUrl } = await createAttachmentUploadUrl(referralId, {
            mimeType: entry.file.type || 'application/octet-stream',
            originalName: entry.originalName,
            type: entry.type,
          });
          await fetch(uploadUrl, {
            method: 'PUT',
            body: entry.file,
            headers: { 'Content-Type': entry.file.type || 'application/octet-stream' },
          });
          const finResult = await finalizeAttachmentUpload(referralId, attachmentId);
          setAttachmentFiles(prev =>
            prev.map(f => f.id === entry.id ? { ...f, status: 'ready', sizeBytes: finResult?.sizeBytes } : f)
          );
        } catch (attErr) {
          console.error('[GateView] Attachment upload failed:', entry.originalName, attErr);
          setAttachmentFiles(prev =>
            prev.map(f => f.id === entry.id ? { ...f, status: 'failed' } : f)
          );
        }
      }

      // Step 4: System referral packet PDF — best-effort, never blocks the receipt.
      // Generates a clinical summary PDF and attaches it at:
      //   phi/attachments/docs/{attachmentId}  (source: SYSTEM_GENERATED)
      // Intake coordinator sees it in the Attachments section automatically.
      try {
        await generateReferralPacketPdf(referralId);
      } catch (pdfGenErr) {
        // Non-fatal: referral is already committed. Log only, do not surface to GP.
        console.warn('[GateView] System PDF generation failed (non-blocking):', pdfGenErr?.message);
      }

      // Step 4.5: Packet promotion + completion (Phase 1) — only when this
      // referral was started from a Pre-Referral Packet. Promotion is NOT
      // atomic with the referral batch (Storage copy can't join the txn).
      // Final success is only shown after completePacket returns COMPLETED.
      if (packetId) {
        const selectedIds = Array.isArray(selectedPacketDocumentIds) ? selectedPacketDocumentIds : [];
        if (selectedIds.length > 0) {
          try {
            const promoteRes = await promotePacketDocuments({
              packetId,
              referralId,
              packetDocumentIds: selectedIds,
            });
            if ((promoteRes?.failed || []).length > 0) {
              setPacketRecovery({
                referralId,
                failedIds: promoteRes.failed,
                message: 'Referral was created, but ' + promoteRes.failed.length +
                  ' packet document(s) were not fully assigned. Retry document assignment.',
              });
              setSubmitResult(result);
              setSubmittedAt(new Date().toISOString());
              // Stop here — do not flip to 'submitted'. Keep user in recovery state.
              return;
            }
            await completePacket({
              packetId,
              referralId,
              packetDocumentIds: selectedIds,
            });
          } catch (packetErr) {
            console.error('[GateView] Packet promotion/completion failed:', packetErr);
            setPacketRecovery({
              referralId,
              failedIds: selectedIds,
              message: 'Referral was created, but packet documents were not fully assigned. Retry document assignment.',
            });
            setSubmitResult(result);
            setSubmittedAt(new Date().toISOString());
            return;
          }
        } else if (isEhrLaunchPacket) {
          // EHR-launch packet submitted with ZERO packet documents (candidate-driven;
          // no uploaded source docs). Skip promotion entirely — promotePacketDocuments
          // rejects an empty array, and there is nothing to promote, so NO attachment
          // manifest is created. Still link the packet to the referral by completing it
          // (the server allows an empty completePacket ONLY for EHR-origin packets).
          try {
            await completePacket({
              packetId,
              referralId,
              packetDocumentIds: [],
            });
          } catch (packetErr) {
            console.error('[GateView] EHR packet completion (no documents) failed:', packetErr);
            setPacketRecovery({
              referralId,
              failedIds: [],
              message: 'Referral was created, but the EHR packet could not be linked. Retry linking.',
            });
            setSubmitResult(result);
            setSubmittedAt(new Date().toISOString());
            return;
          }
        }
      }

      // All steps complete — show receipt
      setSubmittedOverride(override ? { reason: overrideReason, note: overrideNote.trim() || null } : null);
      setSubmittedAt(new Date().toISOString());
      setSubmitResult(result);
      setPhase('submitted');
      // Phase 3 — PHI retry satisfied; clear committed tracking so the next
      // referral starts fresh (and does not skip its own core batch).
      committedReferralIdRef.current = null;
      committedSubmitResultRef.current = null;
      setCommittedReferralId(null);
    } catch (err) {
      console.error('[GateView Error] Submission failed:', err);
      // If the core batch already committed, the failure was in PHI / attachments /
      // packet. The committed referral is preserved — retry completes the SAME
      // referral (no duplicate core/metric/audit/workflow/submission docs).
      if (committedReferralIdRef.current) {
        setSubmitError('Referral shell was created, but patient details did not finish saving. Retry will complete the same referral.');
      } else if (err?.code === 'EHR_PROVENANCE_VIOLATION' || err?.code === 'SENSITIVE_DATA_VIOLATION') {
        // Build 2J P2 — EHR source provenance failed safety validation; nothing was
        // written. Clear, retryable error with no partial referral/audit state.
        setSubmitError('Could not safely record EHR source provenance for this referral. Please retry.');
      } else {
        setSubmitError('Unable to save referral. Please retry.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Export: Copy EHR Note (NO PHI) ─────────────────────────────────────────
  const handleCopyEhrNote = async () => {
    if (!submitResult || !normalizedPack || !liveDecision) return;
    setEhrCopyError(null);
    setEhrFallbackNote(null);
    setEhrCopyLoading(true);
    try {
      const specialtyName  = specialties[selectedSpecialtyId]?.name || normalizedPack.specialtyId || '';
      const requiredFields = (path1Pack?.fields || []).filter(f => f.required);

      // Load current workflow state (non-PHI) for the "Next Step" section.
      // Non-blocking: if this fails, the note is still generated without the next-step line.
      let workflowStatus = null;
      try {
        const wfState = await getWorkflowState(user.tenantId, submitResult.referralId);
        workflowStatus = wfState?.status || null;
      } catch (wfErr) {
        console.warn('[GateView] Could not load workflow state for EHR note (non-blocking):', wfErr);
      }

      const note = buildEhrNoteNoPhi({
        receiptToken:        submitResult.receiptToken || null,
        runId:               submitResult.runId || submitResult.referralId,
        referralId:          submitResult.referralId,
        tenantId:            user.tenantId,
        specialtyName,
        packName:            normalizedPack.name,
        packVersion:         normalizedPack.versionId,
        outcome:             liveDecision.outcomeStatus,
        completenessScore:   liveDecision.completenessScore,
        visitType,
        reasonForEscalation,
        missingFields:       liveDecision.missingRequiredFields || [],
        triggeredRedFlags:   liveDecision.triggeredRedFlags || [],
        requiredFields,
        clinicalAnswers,
        attachmentFiles,
        reworkItemTypes:     [], // empty at submit time; populated after triage review
        overrideInfo:        submittedOverride,
        submittedAt,
        referrerDisplayName: user.displayName || user.name || null,
        workflowStatus,
      });

      // Try clipboard API; fall back to selectable text modal on permission denial
      try {
        await navigator.clipboard.writeText(note);
        setEhrCopied(true);
        setTimeout(() => setEhrCopied(false), 3000);
      } catch (clipErr) {
        console.warn('[GateView] Clipboard write blocked, showing fallback modal:', clipErr);
        setEhrFallbackNote(note);
      }

      // Non-PHI audit log (best-effort)
      writeExportAudit({
        tenantId:   user.tenantId,
        referralId: submitResult.referralId,
        actorUid:   user.uid || user.id,
        actorRole:  user.role || 'gp_employee',
        eventType:  EXPORT_AUDIT_EVENTS.COPIED_NO_PHI,
      });
    } catch (err) {
      console.error('[GateView] EHR note generation failed:', err);
      setEhrCopyError(safeUserError(err));
    } finally {
      setEhrCopyLoading(false);
    }
  };

  // ── Patient Note copy (NO PHI) ────────────────────────────────────────────────
  const handleCopyPatientNote = useCallback(async () => {
    if (!submitResult || !liveDecision) return;
    setPatientNoteCopyError(null);
    const specialtyName = specialties[selectedSpecialtyId]?.name || '';
    const outcome = liveDecision.outcomeStatus;
    const noteText = (outcome === 'not_yet')
      ? buildPatientRequestNoPhi({
          receiptToken:         submitResult.receiptToken || null,
          referralId:           submitResult.referralId,
          specialtyName,
          missingItemTypeLabels: (liveDecision.missingRequiredFields || []).map(f => f.label),
          submittedAt,
        })
      : buildPatientApprovedNoPhi({
          receiptToken: submitResult.receiptToken || null,
          referralId:   submitResult.referralId,
          specialtyName,
          submittedAt,
          outcome,
        });
    try {
      await navigator.clipboard.writeText(noteText);
      setPatientNoteCopied(true);
      setTimeout(() => setPatientNoteCopied(false), 3000);
    } catch (err) {
      setPatientNoteCopyError('Clipboard blocked — copy text manually below.');
    }
  }, [submitResult, liveDecision, specialties, selectedSpecialtyId, submittedAt]);

  // ── Export: Download Full Receipt PDF (PHI) ──────────────────────────────────
  const handleDownloadPhiPdf = async () => {
    if (!submitResult || !normalizedPack || !liveDecision) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const referralId    = submitResult.referralId;
      const specialtyName = specialties[selectedSpecialtyId]?.name || normalizedPack.specialtyId || '';
      const requiredFields = (path1Pack?.fields || []).filter(f => f.required);

      // Load PHI via Cloud Functions — each call logs to phi_access_logs server-side.
      // If this is a resumed referral (completedFromNotYet), also load attachments from the
      // original referral because the new submission creates a fresh referralId while the
      // files remain stored under the original one.
      let phiIdentity = null, phiContact = null, phiInsurance = null, phiAttachs = [];
      try {
        const fetchOriginalAttachs =
          resumeReferralId && resumeReferralId !== referralId
            ? listAttachments(resumeReferralId).catch(() => ({ attachments: [] }))
            : Promise.resolve({ attachments: [] });

        const [idRes, ctRes, insRes, attRes, origAttRes] = await Promise.all([
          getPatientIdentity(referralId),
          getContact(referralId),
          getInsurance(referralId).catch(() => ({ exists: false, data: null })), // optional
          listAttachments(referralId).catch(() => ({ attachments: [] })),        // new submission
          fetchOriginalAttachs,                                                  // original run
        ]);
        phiIdentity  = idRes?.data  || null;
        phiContact   = ctRes?.data  || null;
        phiInsurance = insRes?.data || null;
        // Merge: new-submission attachments first, then original-run attachments (deduped by id)
        const newAttachs  = attRes?.attachments     || [];
        const origAttachs = origAttRes?.attachments || [];
        const seenIds = new Set(newAttachs.map(a => a.attachmentId || a.id));
        const uniqueOrig = origAttachs.filter(a => !seenIds.has(a.attachmentId || a.id));
        phiAttachs = [...newAttachs, ...uniqueOrig];
      } catch (phiErr) {
        // If PHI load fails entirely, block PDF generation
        throw new Error('Unable to load patient data. Check your access or retry.');
      }

      const model = buildFullReceiptModel({
        runId:               submitResult.runId || referralId,
        referralId,
        tenantId:            user.tenantId,
        specialtyName,
        packName:            normalizedPack.name,
        packVersion:         normalizedPack.versionId,
        outcome:             liveDecision.outcomeStatus,
        completenessScore:   liveDecision.completenessScore,
        visitType,
        reasonForEscalation,
        missingFields:       liveDecision.missingRequiredFields || [],
        triggeredRedFlags:   liveDecision.triggeredRedFlags || [],
        requiredFields,
        clinicalAnswers,
        overrideInfo:        submittedOverride,
        submittedAt,
        submittedByName:     user.displayName || user.name || null,
        submittedByRole:     user.role || 'gp_employee',
        phiIdentity,
        phiContact,
        phiInsurance,
        attachments:         phiAttachs,
      });

      await renderReceiptPdf(model);

      // Non-PHI audit log (best-effort)
      writeExportAudit({
        tenantId:   user.tenantId,
        referralId,
        actorUid:   user.uid || user.id,
        actorRole:  user.role || 'gp_employee',
        eventType:  EXPORT_AUDIT_EVENTS.PDF_DOWNLOADED_PHI,
      });
    } catch (err) {
      console.error('[GateView] PDF generation failed:', err);
      setPdfError(safeUserError(err));
    } finally {
      setPdfLoading(false);
    }
  };

  // ============================================================================
  // FIELD RENDERER — Path 1: boolean/enum/structured only. No free text.
  // ============================================================================

  const renderField = (field, value, onChange) => {
    // PATH 1 enforcement: skip any free-text field types
    if (field.type === 'textarea' || field.type === 'text') return null;

    const label = (
      <label className="block text-sm font-medium text-slate-300">
        {field.label}
        {field.required && <span className="text-red-400 ml-1">*</span>}
      </label>
    );
    const help = field.helpText && <p className="text-xs text-slate-500">{field.helpText}</p>;

    if (field.type === 'severity' || field.type === 'slider') {
      return (
        <div key={field.id} className="space-y-2">
          {label}
          <SeveritySlider
            value={value ?? 5}
            onChange={(v) => onChange(field.id, v)}
            min={field.min ?? 0}
            max={field.max ?? 10}
          />
          {help}
        </div>
      );
    }

    if (field.type === 'number') {
      const isSlider = field.min !== undefined && field.max !== undefined &&
        ((field.min === 0 && field.max === 10) || (field.min === 1 && field.max === 10));
      if (isSlider) {
        return (
          <div key={field.id} className="space-y-2">
            {label}
            <SeveritySlider
              value={value ?? 0}
              onChange={(v) => onChange(field.id, v)}
              min={field.min}
              max={field.max}
            />
            {help}
          </div>
        );
      }
      return (
        <div key={field.id} className="space-y-2">
          {label}
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value ? Number(e.target.value) : '')}
            min={field.min} max={field.max} placeholder={field.placeholder}
            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50"
          />
          {help}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.id} className="space-y-2">
          {label}
          <div className="relative">
            <select
              value={value || ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 appearance-none cursor-pointer pr-10"
            >
              <option value="">Select...</option>
              {field.options?.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <SelectChevron />
          </div>
          {help}
        </div>
      );
    }

    if (field.type === 'multiselect') {
      return (
        <div key={field.id} className="space-y-2">
          {label}
          <div className="grid grid-cols-2 gap-2">
            {field.options?.map(opt => {
              const sel = (value || []).includes(opt.value);
              return (
                <button
                  key={opt.value} type="button"
                  onClick={() => {
                    const cur = value || [];
                    onChange(field.id, sel ? cur.filter(v => v !== opt.value) : [...cur, opt.value]);
                  }}
                  className={`px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                    sel
                      ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-300'
                      : 'bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {help}
        </div>
      );
    }

    if (field.type === 'boolean') {
      return (
        <div key={field.id} className="space-y-2">
          <button
            type="button"
            onClick={() => onChange(field.id, !value)}
            className={`w-full p-4 rounded-xl border text-left flex items-center gap-3 transition-colors ${
              value
                ? 'bg-emerald-500/20 border-emerald-500/50'
                : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
            }`}
          >
            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
              value ? 'bg-emerald-500 text-white' : 'bg-slate-700'
            }`}>
              {value ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-slate-500" />}
            </div>
            <span className={`font-medium ${value ? 'text-emerald-300' : 'text-white'}`}>{field.label}</span>
          </button>
          {help}
        </div>
      );
    }

    if (field.type === 'date') {
      return (
        <div key={field.id} className="space-y-2">
          {label}
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 cursor-pointer hover:border-slate-600 transition-colors"
          />
          {help}
        </div>
      );
    }

    // Unknown field type → skip (no fallback free-text input in Path 1)
    return null;
  };

  // ============================================================================
  // RENDER: PATIENT SECTION (always visible at top; collapses after PHI saved)
  // ============================================================================

  const renderPatientSection = () => {
    // While PHI is loading on resume — show a spinner so the form doesn't flash empty
    if (phiLoading) {
      return (
        <div className="flex items-center gap-3 py-2">
          <Loader2 className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0" />
          <p className="text-sm text-slate-400">Loading patient information…</p>
        </div>
      );
    }

    // After patient form is complete — compact confirmation banner with Edit option
    if (phiSaved) {
      return (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white text-sm">Patient info ready</p>
            <p className="text-xs text-slate-400">Will be saved securely when referral is submitted</p>
          </div>
          <button
            onClick={() => setPhiSaved(false)}
            className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-slate-700/50 transition-colors flex-shrink-0"
          >
            Edit
          </button>
        </div>
      );
    }

    return (
      <PatientStepPHI
        initialData={patientFormData}
        packetId={packetId}
        onComplete={(data) => { setPatientFormData(data); setEhrCandidateUsage(data?.ehrCandidateUsage || null); setPhiSaved(true); }}
      />
    );
  };

  // ============================================================================
  // RENDER: SELECT PHASE
  // ============================================================================

  // ============================================================================
  // RENDER: FORM PHASE (single scroll — all sections vertically)
  // ============================================================================

  const renderFormPhase = () => {
    if (!normalizedPack || !path1Pack) return null;

    const escalationOptions = (normalizedPack.escalationOptions || [])
      .filter(o => o !== 'Other' && o !== 'Other reason'); // Enum-only, no free text

    const intraRedirectDefs = normalizedPack.intraSpecialtyRedirectFlags || [];
    const crossRedirectDefs = normalizedPack.crossSpecialtyRedirectFlags || [];

    // ── CTA label derivations ─────────────────────────────────────────────────
    // Intra: "Switch to: {SpecialtyName} - {PackName}"
    const specialtyName = normalizedPack.metadata?.specialty || '';
    const intraCTALabel = (() => {
      const flag = activeRedirectResult.level === 'intra_specialty' ? activeRedirectResult.flag : null;
      if (!flag) return 'Switch Pack';
      const packLabel = flag.target?.label || '';
      if (specialtyName && packLabel) return `Switch to: ${specialtyName} - ${packLabel}`;
      return packLabel ? `Switch to: ${packLabel}` : 'Switch Pack';
    })();

    // Cross: "Go to {SpecialtyLabel} - {PackName}" when pack is resolved; otherwise just specialty
    const crossCTALabel = (() => {
      const specialtyLabel = activeRedirectResult.flag?.target?.label || '';
      if (!crossResolution) return specialtyLabel ? `Go to ${specialtyLabel}` : 'Switch Specialty';
      if (crossResolution.state === 'resolved') {
        const packName = crossResolution.entry?.name ||
                         activeRedirectResult.flag?.target?.targetPackLabel || '';
        if (specialtyLabel && packName) return `Go to ${specialtyLabel} - ${packName}`;
      }
      return specialtyLabel ? `Go to ${specialtyLabel}` : 'Switch Specialty';
    })();
    const redFlags = normalizedPack.redFlags || [];
    // Use path1Pack.fields — already stripped of text/textarea types — for display
    const requiredFields = (path1Pack.fields || []).filter(f => f.required);
    const additionalFields = (path1Pack.fields || []).filter(f => !f.required);

    return (
      <ClinicalEvidenceProvider
        packetId={packetId}
        specialtyId={normalizedPack.specialtyId}
        packId={normalizedPack.packId}
        versionId={normalizedPack.versionId}
        packDocId={normalizedPack.packDocId}
      >
      <div className="space-y-8">

        {/* Pack error boundary */}
        {packLoadError && (
          <PackErrorBoundary
            message={packLoadError}
            onRetry={resetFlow}
          />
        )}

        {/* Pre-Referral Packet context (Phase 1) — appears when GP started
            from a packet. Manual Gate (no packetId) renders nothing here. */}
        {packetId && (
          <PacketContextPanel
            packetId={packetId}
            selectedIds={selectedPacketDocumentIds}
            onChange={setSelectedPacketDocumentIds}
            isEhrLaunch={isEhrLaunchPacket}
          />
        )}

        {/* Clinical evidence (Phase 2C-4): non-blocking status line. Chips are
            read-only pointers rendered beside fields/options/red flags/attachments
            below — they never write answers or change the Gate outcome. */}
        {packetId && <ClinicalEvidenceNote />}

        {/* Recovery card for packet promotion failure (Phase 1). The referral
            has already been created in Firestore; we keep the user here until
            the documents are fully assigned. Retry is idempotent. */}
        {packetRecovery && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
            <div className="font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Document assignment incomplete
            </div>
            <div>{packetRecovery.message}</div>
            <div className="text-xs">
              Referral ID: <span className="font-mono">{packetRecovery.referralId}</span>
              {packetRecovery.failedIds?.length > 0 && (
                <> · Failed documents: {packetRecovery.failedIds.length}</>
              )}
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!packetId || !packetRecovery) return;
                setPacketRetrying(true);
                try {
                  const all = Array.isArray(selectedPacketDocumentIds) ? selectedPacketDocumentIds : [];
                  // EHR-launch packets may have zero documents — skip promotion (it
                  // rejects an empty array) and just (re)link via completePacket.
                  if (all.length > 0) {
                    const promoteRes = await promotePacketDocuments({
                      packetId,
                      referralId: packetRecovery.referralId,
                      packetDocumentIds: all,
                    });
                    if ((promoteRes?.failed || []).length > 0) {
                      setPacketRecovery({
                        referralId: packetRecovery.referralId,
                        failedIds: promoteRes.failed,
                        message: 'Some documents still failed to assign. You may retry again.',
                      });
                      return;
                    }
                  }
                  await completePacket({
                    packetId,
                    referralId: packetRecovery.referralId,
                    packetDocumentIds: all,
                  });
                  setPacketRecovery(null);
                  setPhase('submitted');
                } catch (err) {
                  console.error('[GateView] Packet recovery retry failed:', err);
                  setPacketRecovery({
                    ...packetRecovery,
                    message: 'Retry failed: ' + (err?.message || 'unknown error') + '. You may try again.',
                  });
                } finally {
                  setPacketRetrying(false);
                }
              }}
              disabled={packetRetrying}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              {packetRetrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Retry document assignment
            </button>
          </div>
        )}

        {resumeReferralId && (
          <div
            data-learning-target="gate_missing_info_context_banner"
            className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-300 ${
              activeFocusTarget === 'gate_missing_info_context_banner'
                ? 'bg-amber-500/15 border-amber-400/60 ring-2 ring-amber-400/20'
                : 'bg-amber-500/10 border-amber-500/20'
            }`}
          >
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-300">Completing a previous referral</p>
              <p className="text-xs text-slate-400 mt-0.5">Your previous answers have been pre-filled. Complete the missing fields below and submit.</p>
            </div>
          </div>
        )}

        {/* ===== SECTION: Same Specialty — Routing Check ===== */}
        {intraRedirectDefs.length > 0 && (
          <section
            data-learning-target="gate_routing_section"
            className={activeFocusTarget === 'gate_routing_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="text-base font-semibold text-white">Same Specialty — Routing Check</h3>
              <span className="text-xs text-slate-500">Check if the primary issue fits a different pathway within this specialty</span>
            </div>
            <div className="space-y-3">
              {intraRedirectDefs.map(flag => {
                const isSelected = selectedRoutingFlag?.block === 'intra' && selectedRoutingFlag?.flagId === flag.id;
                return (
                  <button
                    key={flag.id}
                    onClick={() => {
                      setIntraPackSwitchError(null);
                      setCrossPackSwitchError(null);
                      // Radio: clicking already-selected deselects; clicking another replaces.
                      setSelectedRoutingFlag(prev =>
                        prev?.block === 'intra' && prev.flagId === flag.id
                          ? null
                          : { block: 'intra', flagId: flag.id }
                      );
                    }}
                    className={`w-full p-4 rounded-xl border text-left transition-colors ${
                      isSelected
                        ? 'bg-amber-500/20 border-amber-500/50'
                        : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected ? 'bg-amber-500 text-white' : 'bg-slate-700'
                      }`}>
                        {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-slate-500" />}
                      </div>
                      <div>
                        <p className={`font-medium ${isSelected ? 'text-amber-300' : 'text-white'}`}>{flag.label}</p>
                        {flag.description && <p className="text-sm text-slate-500 mt-0.5">{flag.description}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Intra-specialty redirect banner */}
            {activeRedirectResult.matched && activeRedirectResult.level === 'intra_specialty' && (
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/40 rounded-xl">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-amber-300">
                      This referral fits a different {specialtyName || 'specialty'} pathway
                    </p>
                    {activeRedirectResult.flag?.redirectReason && (
                      <p className="text-sm text-slate-300 mt-1">{activeRedirectResult.flag.redirectReason}</p>
                    )}
                    {activeRedirectResult.flag?.target?.label && (
                      <p className="text-sm text-amber-400 mt-1 font-medium">
                        → {intraCTALabel}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      No referral will be created under the wrong pack. Click below to switch pathway in place.
                    </p>
                  </div>
                </div>
                {intraPackSwitchError && (
                  <div className="mb-3 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{intraPackSwitchError}</p>
                  </div>
                )}
                <button
                  onClick={() => handleIntraPackSwitch(activeRedirectResult.flag)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  {intraCTALabel}
                </button>
              </div>
            )}
          </section>
        )}

        {/* ===== SECTION: Different Specialty — Routing Check ===== */}
        {crossRedirectDefs.length > 0 && (
          <section
            data-learning-target={intraRedirectDefs.length === 0 ? 'gate_routing_section' : undefined}
            className={activeFocusTarget === 'gate_routing_section' && intraRedirectDefs.length === 0 ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-violet-400" />
              </div>
              <h3 className="text-base font-semibold text-white">Different Specialty — Routing Check</h3>
              <span className="text-xs text-slate-500">Check if this referral belongs in a different specialty</span>
            </div>
            <div className="space-y-3">
              {crossRedirectDefs.map(flag => {
                const isSelected = selectedRoutingFlag?.block === 'cross' && selectedRoutingFlag?.flagId === flag.id;
                return (
                  <button
                    key={flag.id}
                    onClick={() => {
                      setIntraPackSwitchError(null);
                      setCrossPackSwitchError(null);
                      // Radio: clicking already-selected deselects; clicking another replaces.
                      setSelectedRoutingFlag(prev =>
                        prev?.block === 'cross' && prev.flagId === flag.id
                          ? null
                          : { block: 'cross', flagId: flag.id }
                      );
                    }}
                    className={`w-full p-4 rounded-xl border text-left transition-colors ${
                      isSelected
                        ? 'bg-violet-500/20 border-violet-500/50'
                        : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected ? 'bg-violet-500 text-white' : 'bg-slate-700'
                      }`}>
                        {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-slate-500" />}
                      </div>
                      <div>
                        <p className={`font-medium ${isSelected ? 'text-violet-300' : 'text-white'}`}>{flag.label}</p>
                        {flag.description && <p className="text-sm text-slate-500 mt-0.5">{flag.description}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Cross-specialty redirect banner */}
            {activeRedirectResult.matched && activeRedirectResult.level === 'cross_specialty' && (
              <div className="mt-4 p-4 bg-violet-500/10 border border-violet-500/40 rounded-xl">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-violet-300">This referral belongs in a different specialty</p>
                    {activeRedirectResult.flag?.redirectReason && (
                      <p className="text-sm text-slate-300 mt-1">{activeRedirectResult.flag.redirectReason}</p>
                    )}
                    <p className="text-sm text-violet-400 mt-1 font-medium">→ {crossCTALabel}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      No referral will be created under this specialty. Click below to switch.
                    </p>
                  </div>
                </div>

                {crossPackSwitchError && (
                  <div className="mb-3 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{crossPackSwitchError}</p>
                  </div>
                )}
                <button
                  onClick={handleCrossSpecialtySwitch}
                  disabled={crossResolution?.state === 'error' && !crossResolution?.specialtyId}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-colors ${
                    crossResolution?.state === 'error' && !crossResolution?.specialtyId
                      ? 'bg-slate-600 cursor-not-allowed opacity-60'
                      : 'bg-violet-600 hover:bg-violet-700'
                  }`}
                >
                  <RotateCcw className="w-4 h-4" />
                  {crossCTALabel}
                </button>
              </div>
            )}
          </section>
        )}

        {/* Clinical form sections are hidden while a redirect is active */}
        {hasRedirect ? null : (<>

        {/* ===== SECTION: Visit Information (header fields, required) ===== */}
        <section
          data-learning-target="gate_visit_information_section"
          className={activeFocusTarget === 'gate_visit_information_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clipboard className="w-4 h-4 text-blue-400" />
            </div>
            <h3 className="text-base font-semibold text-white">Visit Information</h3>
          </div>
          <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">

            {/* Visit Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Visit Type <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={visitType}
                  onChange={(e) => setVisitType(e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 appearance-none cursor-pointer pr-10"
                >
                  {VISIT_TYPES.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <SelectChevron />
              </div>
            </div>

            {/* Visit Purpose */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Visit Purpose <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={visitPurpose}
                  onChange={(e) => {
                    setVisitPurpose(e.target.value);
                    if (e.target.value !== 'URGENT_CONSULT') setReasonForEscalation('');
                  }}
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 appearance-none cursor-pointer pr-10"
                >
                  {VISIT_PURPOSE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <SelectChevron />
              </div>
            </div>

            {/* Reason for Escalation — shown only for Urgent Consult, enum only */}
            {visitPurpose === 'URGENT_CONSULT' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Reason for Escalation <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={reasonForEscalation}
                  onChange={(e) => setReasonForEscalation(e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 appearance-none cursor-pointer pr-10"
                >
                  <option value="">Select reason...</option>
                  {escalationOptions.map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                <SelectChevron />
              </div>
            </div>
            )}

          </div>
        </section>

        {/* ===== SECTION: Red Flags ===== */}
        {redFlags.length > 0 && (
          <section
            data-learning-target="gate_red_flags_section"
            className={activeFocusTarget === 'gate_red_flags_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-red-400" />
              </div>
              <h3 className="text-base font-semibold text-white">Red Flags</h3>
              <span className="text-xs text-slate-500">Check any concerning symptoms present</span>
            </div>
            <div className="space-y-3">
              {redFlags.map(flag => {
                const isSelected = selectedRedFlagIds.includes(flag.id);
                return (
                  <div key={flag.id}>
                    <button
                      onClick={() => handleRedFlagToggle(flag.id)}
                      className={`w-full p-4 rounded-xl border text-left transition-colors ${
                        isSelected
                          ? 'bg-red-500/20 border-red-500/50'
                          : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isSelected ? 'bg-red-500 text-white' : 'bg-slate-700'
                        }`}>
                          {isSelected
                            ? <CheckSquare className="w-4 h-4" />
                            : <Square className="w-4 h-4 text-slate-500" />
                          }
                        </div>
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-red-300' : 'text-white'}`}>
                            {flag.label}
                          </p>
                          {flag.description && (
                            <p className="text-sm text-slate-500 mt-0.5">{flag.description}</p>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* Read-only evidence pointer — OUTSIDE the button so a chip
                        click never toggles the red flag. */}
                    <RedFlagEvidenceChips redFlagId={flag.id} />
                  </div>
                );
              })}
            </div>
            {selectedRedFlagIds.length > 0 && (
              <div className="mt-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">
                    {selectedRedFlagIds.length} red flag(s) selected — outcome will be URGENT
                  </span>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ===== SECTION: Required Minimum Workup ===== */}
        {requiredFields.length > 0 && (
          <section
            data-learning-target="gate_required_workup_section"
            className={activeFocusTarget === 'gate_required_workup_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="text-base font-semibold text-white">Required Minimum Workup</h3>
              <span className="text-xs text-red-400 font-medium">All required</span>
            </div>
            <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              {requiredFields.map(field => {
                const node = renderField(field, clinicalAnswers[field.id], handleClinicalChange);
                if (!node) return null;
                return (
                  <div key={field.id}>
                    {node}
                    <FieldEvidenceChips field={field} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ===== SECTION: Additional Structured Fields ===== */}
        {additionalFields.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Clipboard className="w-4 h-4 text-violet-400" />
              </div>
              <h3 className="text-base font-semibold text-white">Additional Clinical Data</h3>
              <span className="text-xs text-slate-500">Optional</span>
            </div>
            <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
              {additionalFields.map(field => {
                const node = renderField(field, clinicalAnswers[field.id], handleClinicalChange);
                if (!node) return null;
                return (
                  <div key={field.id}>
                    {node}
                    <FieldEvidenceChips field={field} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ===== LIVE PREVIEW CARD ===== */}
        <section
          data-learning-target="gate_decision_preview_section"
          className={activeFocusTarget === 'gate_decision_preview_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center">
                <Eye className="w-4 h-4 text-slate-400" />
              </div>
              <h3 className="text-base font-semibold text-slate-300">Decision Preview</h3>
            </div>
            <span className="text-xs text-slate-500 hidden sm:inline">
              Updates live as you fill in the form
            </span>
          </div>
          <div className="mt-3">
            <LivePreviewCard decision={liveDecision} />
          </div>
        </section>

        {/* ===== OVERRIDE PANEL (only when NOT YET) ===== */}
        {liveDecision?.outcomeStatus === 'not_yet' && (
          <section
            data-learning-target="gate_override_section"
            className={activeFocusTarget === 'gate_override_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
          >
            {/* Collapsed toggle */}
            <button
              type="button"
              onClick={() => setOverrideOpen(o => !o)}
              className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
                overrideActive
                  ? 'bg-violet-500/15 border-violet-500/40'
                  : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  overrideActive ? 'bg-violet-500/30' : 'bg-slate-700/50'
                }`}>
                  <ShieldOff className={`w-4 h-4 ${overrideActive ? 'text-violet-400' : 'text-slate-400'}`} />
                </div>
                <div className="text-left">
                  <p className={`text-sm font-semibold ${overrideActive ? 'text-violet-300' : 'text-slate-300'}`}>
                    Override Gate Decision
                    {overrideActive && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-violet-500/30 text-violet-300 font-medium">
                        ACTIVE
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {overrideActive
                      ? `Reason: ${OVERRIDE_REASON_LABELS[overrideReason]}`
                      : 'Clinical judgement override — requires justification'
                    }
                  </p>
                </div>
              </div>
              {overrideOpen
                ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
              }
            </button>

            {/* Expanded panel */}
            {overrideOpen && (
              <div className="mt-2 p-5 bg-slate-800/40 border border-violet-500/30 rounded-xl space-y-4">

                {/* Warning banner */}
                <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300">
                    Overriding a NOT YET decision submits the referral as ROUTINE despite missing required data.
                    This action is logged and auditable.
                  </p>
                </div>

                {/* Override reason */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-300">
                    Override Reason <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={overrideReason}
                      onChange={(e) => {
                        setOverrideReason(e.target.value);
                        setOverrideConfirmed(false); // reset confirmation on reason change
                      }}
                      className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500/50 appearance-none cursor-pointer pr-10"
                    >
                      <option value="">Select reason...</option>
                      {Object.entries(OVERRIDE_REASON_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <SelectChevron />
                  </div>
                </div>

                {/* Override note */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-300">
                    Justification Note
                    <span className="ml-2 text-xs text-slate-500 font-normal">
                      ({overrideNote.length}/{OVERRIDE_NOTE_MAX_LENGTH}) — do not include patient identifiers
                    </span>
                  </label>
                  <textarea
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value.slice(0, OVERRIDE_NOTE_MAX_LENGTH))}
                    rows={3}
                    placeholder="Brief clinical justification (optional, no PHI)..."
                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-violet-500/50 resize-none text-sm"
                  />
                </div>

                {/* Confirmation checkbox */}
                {overrideReason && (
                  <button
                    type="button"
                    onClick={() => setOverrideConfirmed(v => !v)}
                    className={`w-full p-4 rounded-xl border text-left flex items-center gap-3 transition-colors ${
                      overrideConfirmed
                        ? 'bg-violet-500/20 border-violet-500/50'
                        : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                      overrideConfirmed ? 'bg-violet-500 text-white' : 'bg-slate-700'
                    }`}>
                      {overrideConfirmed
                        ? <CheckSquare className="w-4 h-4" />
                        : <Square className="w-4 h-4 text-slate-500" />
                      }
                    </div>
                    <span className={`text-sm font-medium ${overrideConfirmed ? 'text-violet-300' : 'text-slate-300'}`}>
                      I confirm this override is clinically justified and accept full responsibility
                    </span>
                  </button>
                )}

              </div>
            )}
          </section>
        )}

        {/* ===== SECTION: Attachments ===== */}
        <section
          data-learning-target="gate_attachments_section"
          className={activeFocusTarget === 'gate_attachments_section' ? 'learning-focus-highlight-subtle rounded-xl transition-all duration-300' : ''}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center">
              <Paperclip className="w-4 h-4 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-white">Attachments</h3>
            <span className="text-xs text-slate-500">Labs, imaging, and supporting documents</span>
          </div>

          {/* Pack-defined attachment requirements */}
          {normalizedPack?.attachmentRequirements?.length > 0 && (
            <div className="mb-4 space-y-2">
              <input
                ref={packAttachInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tif,.tiff"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0 && pendingAttachTypeRef.current) {
                    handleAddAttachments(files, pendingAttachTypeRef.current);
                  }
                  pendingAttachTypeRef.current = null;
                  e.target.value = '';
                }}
              />
              {normalizedPack.attachmentRequirements.map((att) => {
                const attachedCount = attachmentFiles.filter(f => f.type === att.attachmentType).length;
                return (
                  <div key={att.id}>
                  <div
                    className="flex items-center justify-between bg-slate-800/50 border border-slate-700/40 rounded-lg px-4 py-3"
                  >
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white">{att.label || 'Untitled attachment'}</p>
                        {attachedCount > 0 && (
                          <span className="text-[10px] font-medium text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 rounded px-1.5 py-0.5">
                            {attachedCount} attached
                          </span>
                        )}
                      </div>
                      {att.description && (
                        <p className="text-xs text-slate-400">{att.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {att.requiredForIntakeCompleteness && (
                        <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5 whitespace-nowrap">
                          REQUIRED FOR INTAKE
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                          pendingAttachTypeRef.current = att.attachmentType || 'OTHER';
                          packAttachInputRef.current?.click();
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Attach File
                      </button>
                    </div>
                  </div>
                  {/* Read-only document evidence pointer — never marks the
                      attachment requirement satisfied. */}
                  <AttachmentEvidenceChips attachmentRequirementId={att.id} />
                  </div>
                );
              })}
            </div>
          )}

          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
            <AttachmentsStepPHI
              files={attachmentFiles}
              onAddFiles={handleAddAttachments}
              onRemoveFile={handleRemoveAttachment}
              disabled={isSubmitting}
            />
          </div>
        </section>

        {/* ===== SUBMIT ERROR ===== */}
        {submitError && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{submitError}</p>
            </div>
          </div>
        )}

        {/* ===== SUBMIT BUTTON ===== */}
        <div
          data-learning-target={activeFocusTarget === 'gate_submit_referral_section' ? 'gate_submit_referral_section' : 'gate_missing_info_submit_area'}
          className={`flex items-center justify-between pt-2 pb-4 border-t border-slate-700/30 transition-all duration-300 ${
            activeFocusTarget === 'gate_missing_info_submit_area' || activeFocusTarget === 'gate_submit_referral_section'
              ? 'learning-focus-highlight-subtle rounded-xl px-2 -mx-2'
              : ''
          }`}
        >
          <div className="space-y-1">
            <p className="text-xs text-slate-500">
              {!phiSaved
                ? 'Save patient info above to enable submit'
                : !visitType || !visitPurpose || (visitPurpose === 'URGENT_CONSULT' && !reasonForEscalation)
                ? 'Complete required fields above to submit'
                : hasRedirect
                ? 'Resolve routing redirect above before submitting'
                : overrideActive
                  ? 'Override active — submits as ROUTINE'
                  : liveDecision?.outcomeStatus === 'not_yet'
                  ? 'This referral is missing required information. Submitting now will save it as Not Yet, not as a routine referral.'
                  : 'Ready to submit referral decision'
              }
            </p>
            {liveDecision?.outcomeStatus === 'not_yet' && !overrideActive && (
              <p className="text-xs text-amber-400">
                {(liveDecision.missingRequiredFields || []).map(f => f.label).filter(Boolean).length > 0
                  ? `Missing: ${liveDecision.missingRequiredFields.map(f => f.label).filter(Boolean).join(', ')}`
                  : 'Missing required referral information.'}
              </p>
            )}
            {overrideActive && (
              <div className="flex items-center gap-1.5">
                <ShieldOff className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-xs text-violet-400 font-medium">
                  Gate override: {OVERRIDE_REASON_LABELS[overrideReason]}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl text-white font-semibold transition-colors disabled:cursor-not-allowed ${
              isSubmitting
                ? 'bg-emerald-600 disabled:bg-emerald-600' /* working state: solid + white text/spinner (no dark-on-dark) */
                : overrideActive
                ? 'bg-violet-500 hover:bg-violet-600 disabled:bg-slate-700'
                : (committedReferralId || liveDecision?.outcomeStatus === 'not_yet')
                ? 'bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700'
                : 'bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700'
            }`}
          >
            {isSubmitting ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Submitting...</>
            ) : committedReferralId ? (
              <><RefreshCw className="w-5 h-5" />Retry — Complete Patient Details</>
            ) : overrideActive ? (
              <><ShieldOff className="w-5 h-5" />Submit with Override</>
            ) : liveDecision?.outcomeStatus === 'not_yet' ? (
              <><Clock className="w-5 h-5" />Submit as Not Yet</>
            ) : (
              <><Shield className="w-5 h-5" />Submit Referral Decision</>
            )}
          </button>
        </div>

        </>) /* end: hasRedirect ? null : (<> ... </>) */}

      </div>
      </ClinicalEvidenceProvider>
    );
  };

  // ============================================================================
  // RENDER: SUBMITTED PHASE — inline result card (replaces old Screens 5 + 6)
  // ============================================================================

  // ============================================================================
  // RENDER: SUBMITTED PHASE — Outcome + dual export actions
  // ============================================================================

  const renderSubmittedPhase = () => {
    if (!submitResult || !liveDecision || !normalizedPack) return null;

    const outcome = liveDecision.outcomeStatus;
    const specialtyName = specialties[selectedSpecialtyId]?.name || '';
    const isNotYet = outcome === 'not_yet';
    const isUrgent = outcome === 'urgent';
    const wasOverridden = Boolean(submittedOverride);

    const outcomeConfigs = {
      urgent:  { color: 'red',     label: 'URGENT',   Icon: AlertTriangle, title: 'Urgent Referral Required' },
      routine: { color: 'emerald', label: 'ROUTINE',  Icon: CheckCircle,   title: 'Referral Approved — Routine' },
      not_yet: { color: 'amber',   label: 'NOT YET',  Icon: Clock,         title: 'Denial Receipt — Additional Workup Required' },
    };
    const displayOutcome = wasOverridden ? 'routine' : outcome;
    const cfg = outcomeConfigs[displayOutcome] || outcomeConfigs.routine;
    const { color: c, Icon } = cfg;

    return (
      <div className="space-y-6">

        {/* ── Outcome banner ── */}
        <div
          data-learning-target="submission_outcome_section"
          className={`p-6 rounded-2xl bg-${c}-500/10 border border-${c}-500/30 transition-all duration-300 ${
            activeFocusTarget === 'submission_outcome_section' ? 'learning-focus-highlight-subtle' : ''
          }`}
        >
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-14 h-14 rounded-2xl bg-${c}-500/20 flex items-center justify-center`}>
              <Icon className={`w-7 h-7 text-${c}-400`} />
            </div>
            <div>
              <h3 className={`text-2xl font-bold text-${c}-400`}>{cfg.label}</h3>
              <p className="text-slate-300">{cfg.title}</p>
            </div>
          </div>
          <div className="pt-4 border-t border-slate-700/50 flex gap-6 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Run ID</p>
              <p className="text-white font-mono text-sm">{submitResult.runId || submitResult.referralId}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Specialty · Pack</p>
              <p className="text-white text-sm">{specialtyName} · {normalizedPack.name} v{normalizedPack.versionId}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-0.5">Completeness</p>
              <p className="text-white text-sm">{liveDecision.completenessScore}%</p>
            </div>
          </div>
        </div>

        {/* Override banner */}
        {wasOverridden && (
          <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <ShieldOff className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-violet-300">Gate Override Applied</p>
                <p className="text-sm text-slate-400 mt-0.5">
                  Reason: {OVERRIDE_REASON_LABELS[submittedOverride.reason]}
                </p>
                {submittedOverride.note && (
                  <p className="text-sm text-slate-500 mt-0.5 italic">"{submittedOverride.note}"</p>
                )}
                <p className="text-xs text-slate-600 mt-1">
                  This override is logged and auditable. Submitted as ROUTINE.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* NOT YET: missing items */}
        {isNotYet && !wasOverridden && liveDecision.missingRequiredFields?.length > 0 && (
          <div className="p-5 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <h4 className="font-semibold text-amber-400 mb-3">Missing Required Items</h4>
            <div className="space-y-2">
              {liveDecision.missingRequiredFields.map(f => (
                <div key={f.id} className="flex items-center gap-2 text-sm text-amber-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  {f.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* URGENT: red flags */}
        {isUrgent && liveDecision.triggeredRedFlags?.length > 0 && (
          <div className="p-5 bg-red-500/10 border border-red-500/30 rounded-xl">
            <h4 className="font-semibold text-red-400 mb-3">Red Flags Triggered</h4>
            <div className="space-y-2">
              {liveDecision.triggeredRedFlags.map(rf => (
                <div key={rf.id} className="flex items-center gap-2 text-sm text-red-300">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {rf.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOT YET: Provider Next Steps — what the GP must do */}
        {isNotYet && !wasOverridden && (
          <div className="p-5 bg-amber-500/5 border border-amber-500/25 rounded-xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <CheckSquare className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <h4 className="text-sm font-semibold text-white">Provider Next Steps</h4>
            </div>
            <p className="text-xs text-slate-400 mb-3">
              Complete the following items, then resubmit this referral:
            </p>
            <div className="space-y-2 mb-4">
              {(liveDecision.missingRequiredFields?.length > 0
                ? liveDecision.missingRequiredFields
                : []
              ).map(f => (
                <div key={f.id} className="flex items-start gap-2.5">
                  <Square className="w-4 h-4 text-amber-500/60 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-300">{f.label}</span>
                </div>
              ))}
              {!liveDecision.missingRequiredFields?.length && (
                <p className="text-sm text-slate-400">See not-yet explanation below for required items.</p>
              )}
            </div>
            <div className="pt-3 border-t border-amber-500/15 space-y-1">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1.5">Then choose one:</p>
              <p className="text-xs text-amber-300/80">→ Gather the items above and complete the existing referral</p>
              <p className="text-xs text-slate-400">→ Contact the intake team to discuss this case</p>
              <p className="text-xs text-slate-400">→ Consider alternative care pathways</p>
            </div>
          </div>
        )}

        {/* NOT YET: messaging actions — clipboard-first, zero PHI */}
        {isNotYet && !wasOverridden && (
          <div className="p-5 bg-slate-800/40 border border-slate-700/50 rounded-xl">
            <MessagingCard
              tenantId={user.tenantId}
              referralId={submitResult.referralId}
              specialtyName={specialtyName}
              packName={normalizedPack.name}
              missingItemTypeLabels={(liveDecision.missingRequiredFields || []).map(f => f.label)}
              notYetReasons={(liveDecision.notYetNextSteps || []).map(s => s.label)}
              receiptToken={submitResult.receiptToken || null}
              submittedAt={submittedAt}
              actorUid={user.uid || user.id}
              actorRole={user.role || 'gp_employee'}
              canLogTouch={true}
            />
          </div>
        )}

        {/* ── Referral Outputs — copy/export actions merged into one compact section ── */}
        <div
          data-learning-target="submission_receipt_exports_section"
          className={`transition-all duration-300 ${
            activeFocusTarget === 'submission_receipt_exports_section' ? 'learning-focus-highlight-subtle rounded-xl' : ''
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-slate-700/60 flex items-center justify-center">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-300">Referral Outputs</h3>
          </div>

          {/* desktop: 3 columns · tablet: 2 · mobile: stacked. Equal-height cards
              with bottom-aligned action buttons. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Output 1 — Patient Note (NO PHI) */}
            <div
              data-learning-target="submission_patient_note_section"
              className={`flex flex-col h-full bg-slate-800/40 border border-slate-700/50 rounded-xl p-5 transition-all duration-300 ${
                activeFocusTarget === 'submission_patient_note_section' ? 'learning-focus-highlight-subtle' : ''
              }`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm">Patient Note</p>
                  <p className="text-xs text-emerald-400 font-medium mt-0.5">NO PHI <span className="text-slate-500 font-normal">· Patient-facing message</span></p>
                </div>
              </div>
              <div className="mt-auto">
                {patientNoteCopyError && (
                  <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-400">{patientNoteCopyError}</p>
                  </div>
                )}
                <button
                  onClick={handleCopyPatientNote}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    patientNoteCopied
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                >
                  {patientNoteCopied
                    ? <><Copy className="w-4 h-4" /> ✓ Copied to clipboard!</>
                    : <><Copy className="w-4 h-4" /> Copy Patient Note (NO PHI)</>
                  }
                </button>
              </div>
            </div>

            {/* Output 2 — EHR Note (NO PHI) */}
            <div className="flex flex-col h-full bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Copy className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm">EHR Note</p>
                  <p className="text-xs text-emerald-400 font-medium mt-0.5">NO PHI <span className="text-slate-500 font-normal">· Safe EHR receipt</span></p>
                </div>
              </div>
              <div className="mt-auto">
                {ehrCopyError && (
                  <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-400">{ehrCopyError}</p>
                  </div>
                )}
                <button
                  onClick={handleCopyEhrNote}
                  disabled={ehrCopyLoading}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    ehrCopied
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : ehrCopyLoading
                        ? 'bg-emerald-500/50 text-white cursor-not-allowed'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                >
                  {ehrCopyLoading
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing note…</>
                    : ehrCopied
                      ? <><Copy className="w-4 h-4" /> ✓ Copied to clipboard!</>
                      : <><Copy className="w-4 h-4" /> Copy EHR Note (NO PHI)</>
                  }
                </button>
              </div>
            </div>

            {/* Output 3 — Full Receipt PDF (PHI) — warning kept at the risky action */}
            <div className="flex flex-col h-full bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <Download className="w-4 h-4 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm">Full Receipt PDF</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Lock className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    <p className="text-xs text-amber-400 font-medium">Contains PHI — access logged</p>
                  </div>
                </div>
              </div>
              <div className="mt-auto">
                {pdfError && (
                  <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-400">{pdfError}</p>
                    <button
                      onClick={() => setPdfError(null)}
                      className="text-xs text-slate-400 hover:text-white mt-1 underline"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
                <button
                  onClick={handleDownloadPhiPdf}
                  disabled={pdfLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/60 disabled:cursor-not-allowed text-white transition-colors"
                >
                  {pdfLoading
                    ? <><Loader2 className="w-4 h-4 animate-spin" />Loading patient data…</>
                    : <><Download className="w-4 h-4" />Download Full Receipt PDF (PHI)</>
                  }
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* Post-submit actions */}
        <div
          data-learning-target="submission_next_actions_section"
          className={`flex justify-center gap-3 pt-6 mt-2 pb-4 border-t border-slate-700/50 transition-all duration-300 ${
            activeFocusTarget === 'submission_next_actions_section' ? 'learning-focus-highlight-subtle rounded-xl p-3' : ''
          }`}
        >
          {!isIntakeCoordinator(user?.role) && (
            <button
              onClick={resetFlow}
              className="flex items-center gap-2 px-6 py-3 bg-slate-800/60 hover:bg-slate-700 border border-slate-700 rounded-xl text-slate-200 font-medium transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Start New Referral
            </button>
          )}
          {!isIntakeCoordinator(user?.role) && onNavigate && (
            isGpEmployee(user?.role) ? (
              // GP home is now Packet Inbox — the completed-referral CTA returns there.
              <button
                onClick={() => onNavigate('packet-inbox')}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-white font-medium transition-colors"
              >
                <ClipboardList className="w-5 h-5" />
                Back to Packet Inbox
              </button>
            ) : (
              <button
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-white font-medium transition-colors"
              >
                <Home className="w-5 h-5" />
                Go to Dashboard
              </button>
            )
          )}
          {isIntakeCoordinator(user?.role) && onNavigate && (
            <button
              onClick={() => onNavigate('workqueue')}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-white font-medium transition-colors"
            >
              <ClipboardList className="w-5 h-5" />
              Go to Workqueue
            </button>
          )}
        </div>

      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (userIsSpecialist && allowedSpecialtyIds?.length === 0) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-400" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">No Specialties Assigned</h2>
          <p className="text-slate-400">Contact your administrator to get access.</p>
        </div>
      </div>
    );
  }

  // Full-page loader while resuming a Not-Yet referral (PHI + pack loading)
  if (isResuming) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
        <p className="text-slate-400 text-sm font-medium">Loading patient information…</p>
      </div>
    );
  }

  // Submitted: full-page receipt
  if (phase === 'submitted') {
    return (
      <div key="submitted" className="p-4 md:p-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium text-emerald-400 uppercase">Referral Gate</span>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">Referral Submitted</h1>
        </div>
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
          {renderSubmittedPhase()}
        </div>

        {/* Clipboard-fail fallback modal — shown when navigator.clipboard is blocked */}
        {ehrFallbackNote && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
            <div className="bg-slate-800 border border-slate-600 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[80vh]">
              <div className="flex items-center justify-between p-5 border-b border-slate-700">
                <div>
                  <p className="font-semibold text-white">EHR Note — Copy Manually</p>
                  <p className="text-xs text-slate-400 mt-0.5">Clipboard access was blocked. Select all text below and copy.</p>
                </div>
                <button
                  onClick={() => setEhrFallbackNote(null)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700 transition-colors"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <textarea
                readOnly
                className="flex-1 font-mono text-xs text-slate-200 bg-slate-900 p-4 resize-none focus:outline-none min-h-[300px]"
                value={ehrFallbackNote}
                onFocus={e => e.target.select()}
              />
              <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
                <button
                  onClick={() => setEhrFallbackNote(null)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Single-scroll main flow
  return (
    <div key="form" className="p-4 md:p-8">
      {/* Page header */}
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
          <Shield className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-medium text-emerald-400 uppercase">Referral Gate</span>
        </div>
        <h1 className="text-2xl font-semibold text-white mb-1">New Referral</h1>
        <p className="text-slate-400 text-sm">Complete all sections below and submit.</p>
      </div>

      <div className="space-y-4">

        {/* ── SECTION 1: Patient Information ── */}
        <div
          data-learning-target="gate_patient_information_section"
          className={`bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 ${activeFocusTarget === 'gate_patient_information_section' ? 'learning-focus-highlight-subtle transition-all duration-300' : ''}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              phiSaved ? 'bg-emerald-500/20' : 'bg-blue-500/20'
            }`}>
              <User className={`w-4 h-4 ${phiSaved ? 'text-emerald-400' : 'text-blue-400'}`} />
            </div>
            <h2 className="text-base font-semibold text-white">Patient Information</h2>
          </div>
          {renderPatientSection()}
        </div>

        {/* ── SECTION 2: Referral Routing ── */}
        <div
          data-learning-target="gate_pathway_selection_section"
          className={`bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 ${activeFocusTarget === 'gate_pathway_selection_section' ? 'learning-focus-highlight-subtle transition-all duration-300' : ''}`}
        >
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                normalizedPack ? 'bg-emerald-500/20' : 'bg-blue-500/20'
              }`}>
                <FileText className={`w-4 h-4 ${normalizedPack ? 'text-emerald-400' : 'text-blue-400'}`} />
              </div>
              <h2 className="text-base font-semibold text-white">Referral Routing</h2>
            </div>

            {loading ? (
              <div className="flex items-center gap-3 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-400 flex-shrink-0" />
                <p className="text-slate-400 text-sm">Loading pathways...</p>
              </div>
            ) : loadError ? (
              <ErrorPanel title="Unable to load pathway standards" message={loadError} onRetry={loadData} />
            ) : normalizedPack ? (
              /* Compact selected-pack summary */
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white">{normalizedPack.name}</p>
                  <p className="text-sm text-slate-400">
                    {specialties[selectedSpecialtyId]?.name || ''} · v{normalizedPack.versionId}
                  </p>
                </div>
                <button
                  onClick={handleChangePack}
                  className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-slate-700/50 transition-colors flex-shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <RoutingSelector
                specialties={filteredSpecialties}
                rawPacks={rawPacks}
                onCommit={handleRoutingCommit}
                draftSaving={draftSaving}
                draftSaveError={draftSaveError}
                invalidDraft={draftInvalid}
                initialSpecialtyId={redirectFallbackSpecialtyId}
              />
            )}
          </div>

        {/* ── SECTION 3: Clinical Form ── */}
        {normalizedPack && (
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Clipboard className="w-4 h-4 text-blue-400" />
              </div>
              <h2 className="text-base font-semibold text-white">Clinical Form</h2>
            </div>
            {renderFormPhase()}
          </div>
        )}

      </div>
    </div>
  );
};

export default GateView;
